/*****************************************************
 * Packages
 ******************************************************/
import ReactGA from 'react-ga';
import React, { useEffect } from 'react';
import { Link, Redirect, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

/*****************************************************
 * Locals
 ******************************************************/
import LoginStc, { SignUpWrap, GoogleSignUp } from './SignUp.stc';
import Button from '../Button';
import Input from '../Input';
import Text from '../Text';

/*****************************************************
 * Actions, Selectors
 ******************************************************/
import * as actions from '../../redux/actions';
import { getIsFetching } from '../../redux/selectors/user';
import {
    getAuthUser,
    getAuthError,
    getAuthSuccess,
} from '../../redux/selectors';
import Field from '../Field';
import Label from '../Label';
import { DorikLogo } from '../Login/Login';
import useScript from '../../hooks/useScript';
import { Helmet } from 'react-helmet';
import { useCallback } from 'react';

function SignUp() {
    const location = useLocation();
    const dispatch = useDispatch();
    const authUser = useSelector(getAuthUser);
    const isLoading = useSelector(getIsFetching);
    const errorMessage = useSelector(getAuthError);
    const successMessage = useSelector(getAuthSuccess);
    useScript('https://www.google.com/recaptcha/api.js');

    const { from } = location.state || { from: { pathname: '/' } };

    const [state, setState] = React.useState({
        email: '',
        password: '',
        confirmPassword: '',
    });

    const invalidInputs =
        !state.email.trim() ||
        !state.password ||
        state.password.length < 6 ||
        state.password !== state.confirmPassword;

    useScript(
        'https://apis.google.com/js/platform.js?onload=renderGoogleSigninButton'
    );

    useEffect(() => {
        window.onGoogleSignIn = (googleUser) => {
            const id_token = googleUser.getAuthResponse().id_token;
            dispatch(
                actions.loginWithRequest({
                    provider: 'google',
                    passthrough: id_token,
                })
            );
        };
    }, [dispatch]);

    useEffect(() => {
        return () => dispatch(actions.clearUserMessage());
    }, []); // unmount

    const handleInput = (e) => {
        const { name, value } = e.target;
        setState({
            ...state,
            [name]: value,
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (invalidInputs) return;
        window.grecaptcha.execute();
        ReactGA.event({
            category: 'User',
            action: 'SignUp Started',
        });
    };

    window.__onSubmitRecaptcha = (recaptchaToken) => {
        const { search } = location;
        const searchParams = new URLSearchParams(search);
        dispatch(
            actions.createUser({
                ...state,
                passthrough: recaptchaToken,
                email: state.email.toLowerCase(),
                promo: searchParams.get('promo'),
            })
        );
    };

    const handleGoogleSignInSuccess = useCallback(
        (googleUser) => {
            const id_token = googleUser.getAuthResponse().id_token;
            dispatch(
                actions.loginWithRequest({
                    provider: 'google',
                    passthrough: id_token,
                })
            );
        },
        [dispatch]
    );

    const handleGoogleSignInFailure = useCallback(() => {
        // ..
    }, []);

    useEffect(() => {
        window.renderGoogleSigninButton = function () {
            window.gapi.signin2.render('google-signin2', {
                scope: 'profile email',
                height: 40,
                width: 240,
                longtitle: true,
                theme: 'dark',
                onsuccess: handleGoogleSignInSuccess,
                onfailure: handleGoogleSignInFailure,
            });
        };
    }, [handleGoogleSignInFailure, handleGoogleSignInSuccess]);

    if (authUser) {
        return <Redirect to={from} />;
    }

    return (
        <SignUpWrap>
            <Helmet>
                <meta
                    name="google-signin-client_id"
                    content="1039322478589-a7g40127ubiog8vorh8c3llj999e92st.apps.googleusercontent.com"
                ></meta>
            </Helmet>
            <DorikLogo />
            <LoginStc>
                <h2>Create an Account</h2>
                <p>
                    Have an Account? <Link to="/login">Login!</Link>
                </p>
                <GoogleSignUp id="google-signin2"></GoogleSignUp>

                <form method="POST" onSubmit={handleSubmit}>
                    <Field>
                        <Label>Email Address</Label>
                        <Input
                            type="email"
                            name="email"
                            placeholder="Email Address"
                            className="mb-15"
                            value={state.email}
                            onChange={handleInput}
                            icon={['far', 'envelope']}
                        />
                    </Field>
                    <Field>
                        <Label>Password</Label>
                        <Input
                            type="password"
                            name="password"
                            className="mb-15"
                            value={state.password}
                            placeholder="Password"
                            onChange={handleInput}
                            icon={['far', 'lock-alt']}
                        />
                    </Field>
                    <Field>
                        <Label>Confirm Password</Label>
                        <Input
                            type="password"
                            name="confirmPassword"
                            className="mb-15"
                            value={state.confirmPassword}
                            onChange={handleInput}
                            icon={['far', 'lock-alt']}
                            placeholder="Confirm Password"
                        />
                    </Field>
                    <div
                        data-size="invisible"
                        data-badge="bottomleft"
                        className="g-recaptcha"
                        style={{ marginBottom: 20 }}
                        data-callback="__onSubmitRecaptcha"
                        data-sitekey="6LfxVrIZAAAAAMYckwABoEPexND9lD-YhzjDIu3Z"
                    ></div>
                    <Button
                        size="3x"
                        fullWidth
                        className="mb-15"
                        onClick={handleSubmit}
                        disabled={invalidInputs || isLoading}
                    >
                        {!isLoading ? (
                            'Create Account'
                        ) : (
                            <>
                                Creating...{' '}
                                <FontAwesomeIcon
                                    spin
                                    icon={['fal', 'spinner']}
                                />
                            </>
                        )}
                    </Button>

                    {(authUser || errorMessage) && (
                        <Text type={authUser ? 'success' : 'error'}>
                            {authUser ? successMessage : errorMessage}
                        </Text>
                    )}
                    <span style={{ fontSize: 14 }}>
                        By creating an account, you agree to the{' '}
                        <a
                            href="https://help.dorik.io/general/terms-and-conditions"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            terms of service
                        </a>{' '}
                        and{' '}
                        <a
                            href="https://help.dorik.io/general/privacy-policy"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            privacy policy
                        </a>
                        .
                    </span>
                </form>
            </LoginStc>
        </SignUpWrap>
    );
}

export default SignUp;
